import { useEffect, useState, type FormEvent } from 'react'

import { ApiError, api, can, refusalReason, shortDate, type ObservationsView, type StoredObservation } from '../lib/api'
import { Status, Toggletip, type Tone } from './kit'
import { ReasonTip } from './vendor/ReasonTip'
import '../styles/website-checks.css'

// Website checks: what this supplier's own website and public records show to
// any visitor, on the vendor's Risk tab.
//
// # WHY IT MOVED AND WHY IT IS STILL HERE
//
// The product officer's review of 21 September (T11) asked for "Seen from
// outside" to leave the vendor overview. Decision 3 of the wave 3 design keeps
// it, on the Risk tab: it is the product's observed-risk signal, a failed
// check is a finding on this vendor like any other, and this is the only place
// a vendor's website is recorded. So everything it did stays (recording or
// confirming the website, the checks and their state, stopping them) and what
// changed is how much it says. Each check is one line with its state; what was
// seen sits behind the (i), and the boundary of what is looked at is one hover
// on the heading. A customer whose supplier asks what we did still has the
// answer one press away.
//
// # WHY A SUGGESTION IS NOT A WEBSITE
//
// A domain read out of a contact's email address is a guess, and checking a
// guess risks pointing this at a shared mail provider or at whoever owns a
// lookalike address. It is offered, and nothing is checked until somebody
// presses the button that says yes.

const VERDICT: Record<StoredObservation['verdict'], { text: string; tone: Tone }> = {
  FAIL: { text: 'A problem', tone: 'crit' },
  WARN: { text: 'Worth asking', tone: 'warn' },
  UNKNOWN: { text: 'Could not tell', tone: 'neutral' },
  PASS: { text: 'Fine', tone: 'ok' },
}

function describe(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.issues.length > 0) return err.issues.map((i) => i.detail).join(' ')
    return err.message
  }
  return err instanceof Error ? err.message : String(err)
}

// What was seen, with the reading itself where there is one, for the (i).
function seen(o: StoredObservation): string {
  const detail = o.detail.trim()
  if (!o.value) return detail
  return detail + (detail.endsWith('.') ? ' ' : '. ') + 'Seen: ' + o.value
}

// The newest moment any check was made, which is when the sweep last looked.
function lastChecked(results: StoredObservation[]): string {
  return results.reduce((newest, o) => (o.observedAt > newest ? o.observedAt : newest), '')
}

export function VendorObservations({ vendorId }: { vendorId: string }) {
  const [view, setView] = useState<ObservationsView | null>(null)
  const [error, setError] = useState('')
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')
  const [busy, setBusy] = useState(false)

  function load() {
    api
      .observations(vendorId)
      .then((v) => {
        setView(v)
        setDraft(v.domain)
      })
      .catch((e) => setError(describe(e)))
  }
  useEffect(load, [vendorId])

  async function save(domain: string, confirming: boolean, e?: FormEvent) {
    e?.preventDefault()
    setBusy(true)
    setError('')
    try {
      await api.setVendorDomain(vendorId, domain, confirming)
      setEditing(false)
      load()
    } catch (err) {
      setError(describe(err))
    } finally {
      setBusy(false)
    }
  }

  if (!view) {
    return error ? (
      <section className="panel vd-web" aria-labelledby="vd-web-h">
        <h2 id="vd-web-h">Website checks</h2>
        <p className="note warn">{error}</p>
      </section>
    ) : null
  }
  const mayEdit = can('vendor.edit')
  const locked = mayEdit ? undefined : refusalReason('vendor.edit')
  const results = view.observations
  // A website read from a contact's address and stored unconfirmed is asked
  // about exactly like a suggestion: nothing is checked until somebody says
  // yes.
  const unconfirmed = view.domain !== '' && view.domainSource === 'SUGGESTED'
  const ask = unconfirmed ? view.domain : !view.domain ? view.suggestion : ''
  const checked = lastChecked(results)

  return (
    <section className="panel vd-web" aria-labelledby="vd-web-h">
      <div className="vd-web-head">
        <h2 id="vd-web-h">Website checks</h2>
        <Toggletip hover label="What is checked">
          Only what their website and public records show any visitor. Nothing is sent to them and they are not told.
        </Toggletip>
        <span className="vd-grow" />
        {view.domain && !unconfirmed && !editing && mayEdit && (
          <button type="button" className="linkish" onClick={() => setEditing(true)}>
            Change
          </button>
        )}
      </div>

      {error && <p className="note warn">{error}</p>}

      {!editing && ask !== '' && (
        <div className="vd-web-ask">
          <p>
            Is <span className="kit-mono">{ask}</span> their website?
          </p>
          <div className="vd-web-actions">
            <button
              type="button"
              className="primary"
              disabled={busy || !mayEdit}
              title={locked}
              onClick={() => void save(ask, true)}
              data-action="tprm.vendor,vendor-record.a20"
            >
              {busy ? 'Saving' : 'Yes, check it'}
            </button>
            <button type="button" disabled={busy || !mayEdit} title={locked} onClick={() => setEditing(true)}>
              No, another address
            </button>
          </div>
        </div>
      )}

      {!editing && ask === '' && !view.domain && (
        <div className="vd-web-ask">
          <p className="vd-quiet">No website recorded</p>
          <div className="vd-web-actions">
            <button type="button" disabled={!mayEdit} title={locked} onClick={() => setEditing(true)}>
              Record their website
            </button>
          </div>
        </div>
      )}

      {editing && (
        <form className="vd-web-form" onSubmit={(e) => void save(draft, false, e)}>
          <label htmlFor="vd-web-domain">Their website</label>
          <div className="vd-web-row">
            <input
              id="vd-web-domain"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="acmecloud.example"
              autoComplete="off"
              spellCheck={false}
            />
            <button type="submit" className="primary" disabled={busy || draft.trim() === ''}>
              {busy ? 'Saving' : 'Save'}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                setEditing(false)
                setDraft(view.domain)
                setError('')
              }}
            >
              Cancel
            </button>
          </div>
          {view.domain && !unconfirmed && (
            <button type="button" className="linkish vd-web-stop" disabled={busy} onClick={() => void save('', false)}>
              Stop checking this website
            </button>
          )}
        </form>
      )}

      {view.domain && !unconfirmed && !editing && (
        <>
          <p className="vd-web-site">
            <span className="kit-mono">{view.domain}</span>
            {results.length > 0 ? (
              <>
                {view.summary.failing > 0 && <Status tone="crit">{view.summary.failing} a problem</Status>}
                {view.summary.warning > 0 && <Status tone="warn">{view.summary.warning} worth asking</Status>}
                {view.summary.passing > 0 && <Status tone="ok">{view.summary.passing} fine</Status>}
                {view.summary.unknown > 0 && <Status tone="neutral">{view.summary.unknown} could not tell</Status>}
                {checked && <span className="vd-quiet">Checked {shortDate(checked)}</span>}
              </>
            ) : (
              <span className="vd-inline vd-quiet">
                Not checked yet
                <Toggletip hover label="When it is checked">
                  The checks run through every recorded website over the course of a day.
                </Toggletip>
              </span>
            )}
          </p>
          {results.length > 0 && (
            <ul className="vd-web-list" aria-label={'Checks on ' + view.domain}>
              {results.map((o) => {
                const verdict = VERDICT[o.verdict] ?? { text: o.verdict, tone: 'neutral' as Tone }
                return (
                  <li key={o.checkKey}>
                    <span className="vd-web-check">{o.label}</span>
                    <Status tone={verdict.tone}>{verdict.text}</Status>
                    <ReasonTip hover label={'What was seen: ' + o.label} text={seen(o)} />
                  </li>
                )
              })}
            </ul>
          )}
        </>
      )}
    </section>
  )
}
